import { useState, useEffect, useMemo, useCallback } from "react";
import {
    Box, Paper, Typography, FormControl, InputLabel, Select, MenuItem,
    Button, Chip, Stack, IconButton, Tooltip, CircularProgress,
    Table, TableBody, TableCell, TableContainer, TableRow,
    Tabs, Tab, Toolbar, TextField, Alert, InputAdornment
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import CheckIcon from "@mui/icons-material/Check";
import SearchIcon from "@mui/icons-material/Search";
import api from "../../api";
import SnackBarComponent from "../../components/SnackBarComponent";
import { useDebounce } from "../../hooks/useDebounce";
import DeleteConfirmDialog from "../../components/DeleteConfirmDialog";
import EnhancedTableHead from "../../components/EnhancedTableHead";
import TablePaginationControls from "../../components/TablePaginationControls";
import { getComparator } from "../../components/tableUtils";

const headCells = [
    { id: "city", label: "City", disablePadding: false, numeric: false },
    { id: "date", label: "Date", disablePadding: false, numeric: false },
    { id: "approvedBy", label: "Status", disablePadding: false, numeric: false },
    { id: "actions", label: "Actions", disablePadding: false, numeric: true },
];


export default function HomeTestRide() {
    const isAdmin = localStorage.getItem("isAdmin") === "true";
    // City state
    const [cities, setCities] = useState([]);
    const [selectedCity, setSelectedCity] = useState("");
    const [citiesLoading, setCitiesLoading] = useState(true);

    // Date picker state
    const [dateInput, setDateInput] = useState("");
    const [pendingDates, setPendingDates] = useState([]); // dates queued to save

    // Off-days table state
    const [offDays, setOffDays] = useState([]);
    const [offDaysLoading, setOffDaysLoading] = useState(false);

    // Saving state
    const [saving, setSaving] = useState(false);

    // Tab state: 0 = Current, 1 = Archive
    const [activeTab, setActiveTab] = useState(0);

    // Sorting state
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("date");

    // Search state
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearchQuery] = useDebounce(searchQuery, 300);

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Delete confirmation dialog
    const [deleteTarget, setDeleteTarget] = useState(null); // { city, date }
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    // Snackbar
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

    // Convert YYYY-MM-DD → epoch seconds (UTC midnight)
    const dateToEpoch = (dateStr) => Math.floor(new Date(dateStr + "T00:00:00Z").getTime() / 1000);

    const showSnackbar = (message, severity = "success") => {
        setSnackbar({ open: true, message, severity });
    };

    // Fetch Cities 
    useEffect(() => {
        const fetchCities = async () => {
            setCitiesLoading(true);
            try {
                const res = await api.get("/api/htr/cities");
                const list = res.data.data;
                setCities(list);
            } catch (err) {
                console.error("Failed to fetch cities:", err);
                showSnackbar("Failed to load cities.", "error");
            } finally {
                setCitiesLoading(false);
            }
        };
        fetchCities();
    }, []);

    // Fetch Off Days when city changes 
    const fetchOffDays = useCallback(async (city) => {
        if (!city) {
            setOffDays([]);
            return;
        }
        setOffDaysLoading(true);
        try {
            const res = await api.get("/api/htr/off-days", { params: { city } });
            const list = res.data?.data ?? res.data ?? [];
            setOffDays(list);
        } catch (err) {
            console.error("Failed to fetch off-days:", err);
            showSnackbar("Failed to load off-days.", "error");
        } finally {
            setOffDaysLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchOffDays(selectedCity);
        setPendingDates([]); // clear pending when city changes
    }, [selectedCity, fetchOffDays]);

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleTabChange = (_, newValue) => {
        setActiveTab(newValue);
        setPage(0);
        setSearchQuery("");
    };

    // Add date to pending list 
    const handleAddDate = () => {
        if (!dateInput) {
            showSnackbar("Please select a date first.", "warning");
            return;
        }
        if (pendingDates.includes(dateInput)) {
            showSnackbar("Date already added.", "warning");
            return;
        }
        // Check if this off-day already exists for this city (compare as epoch)
        const epoch = dateToEpoch(dateInput);
        if (offDays.some(od => od.city === selectedCity && Number(od.date) === epoch)) {
            showSnackbar("This date is already marked as an off-day for this city.", "warning");
            return;
        }
        setPendingDates(prev => [...prev, dateInput].sort());
        setDateInput("");
    };

    const handleRemovePendingDate = (date) => {
        setPendingDates(prev => prev.filter(d => d !== date));
    };

    // Save pending dates 
    const handleSave = async () => {
        if (!selectedCity) {
            showSnackbar("Please select a city.", "warning");
            return;
        }
        if (pendingDates.length === 0) {
            showSnackbar("Please add at least one date.", "warning");
            return;
        }

        setSaving(true);
        try {
            await Promise.all(
                pendingDates.map(date =>
                    api.post("/api/htr/off-days", { city: selectedCity, date: dateToEpoch(date) })
                )
            );
            showSnackbar(`${pendingDates.length} off-day(s) saved successfully!`, "success");
            setPendingDates([]);
            fetchOffDays(selectedCity);
        } catch (err) {
            console.error("Save failed:", err);
            showSnackbar(err.response?.data?.msg || "Failed to save off-days.", "error");
        } finally {
            setSaving(false);
        }
    };

    // --- Delete off-day ---
    const handleDeleteClick = (offDay) => {
        setDeleteTarget(offDay);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        try {
            await api.delete("/api/htr/off-days", {
                data: { city: deleteTarget.city, date: deleteTarget.date }
            });
            showSnackbar("Off-day deleted successfully.", "success");
            setOffDays(prev => prev.filter(od => !(od.city === deleteTarget.city && od.date === deleteTarget.date)));
        } catch (err) {
            console.error("Delete failed:", err);
            showSnackbar(err.response?.data?.msg || "Failed to delete off-day.", "error");
        } finally {
            setDeleteDialogOpen(false);
            setDeleteTarget(null);
        }
    };

    // --- Approve off-day ---
    const handleApproveClick = async (offDay) => {
        try {
            await api.put("/api/htr/off-days/approve", { city: offDay.city, date: offDay.date });
            showSnackbar("Off-day approved successfully.", "success");
            fetchOffDays(selectedCity);
        } catch (err) {
            console.error("Approve failed:", err);
            showSnackbar(err.response?.data?.msg || "Failed to approve off-day.", "error");
        }
    };

    const formatDate = (dateVal) => {
        if (!dateVal && dateVal !== 0) return "";
        let d;
        if (typeof dateVal === "number" || (typeof dateVal === "string" && /^\d+$/.test(dateVal))) {
            d = new Date(Number(dateVal) * 1000);
        } else {
            d = new Date(dateVal + "T00:00:00Z");
        }
        return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" });
    };

    // Filtered + sorted + paginated off-days
    const visibleOffDays = useMemo(() => {
        const todaySec = (Date.now() - 86400000) / 1000;
        const q = debouncedSearchQuery.toLowerCase();

        const filtered = offDays.filter((od) => {
            // Tab filter (date field stored as epoch seconds)
            const dateSec = Number(od.date);
            const isCurrentTab = activeTab === 0 ? dateSec >= todaySec : dateSec < todaySec;
            if (!isCurrentTab) return false;

            // Search filter
            if (!q) return true;
            const status = od.approvedBy ? "approved" : "pending";
            return (
                (od.city || "").toLowerCase().includes(q) ||
                formatDate(od.date).toLowerCase().includes(q) ||
                status.includes(q)
            );
        });

        // Sort
        return [...filtered].sort((a, b) => {
            if (orderBy === "approvedBy") {
                const aVal = a.approvedBy ? "approved" : "pending";
                const bVal = b.approvedBy ? "approved" : "pending";
                const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
                return order === "asc" ? cmp : -cmp;
            }
            return getComparator(order, orderBy)(a, b);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offDays, debouncedSearchQuery, order, orderBy, activeTab]);

    const paginatedOffDays = useMemo(() => {
        return visibleOffDays.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [visibleOffDays, page, rowsPerPage]);

    return (
        <Box sx={{ width: "100%" }}>
            {snackbar.open && (
                <SnackBarComponent
                    message={snackbar.message}
                    severity={snackbar.severity}
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                />
            )}

            <Paper sx={{ width: "100%", mb: 3 }}>
                <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 } }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <EventBusyIcon color="primary" />
                        <Typography variant="h6" component="div">
                            HOME TEST RIDE OFFDAYS
                        </Typography>
                    </Box>
                </Toolbar>

                <Box sx={{ px: 3, pb: 3, display: "flex", flexDirection: "column", gap: 3 }}>

                    {/* City Dropdown */}
                    <FormControl fullWidth>
                        <InputLabel id="city-select-label">Select City</InputLabel>
                        <Select
                            labelId="city-select-label"
                            id="city-select"
                            value={selectedCity}
                            label="Select City"
                            onChange={(e) => setSelectedCity(e.target.value)}
                            disabled={citiesLoading}
                        >
                            {citiesLoading ? (
                                <MenuItem disabled>
                                    <CircularProgress size={16} sx={{ mr: 1 }} /> Loading cities…
                                </MenuItem>
                            ) : cities.length === 0 ? (
                                <MenuItem disabled>No cities available</MenuItem>
                            ) : (
                                cities.map((city) => {
                                    const cityName = typeof city === "string" ? city : (city.city || city.name || JSON.stringify(city));
                                    return (
                                        <MenuItem key={cityName} value={cityName}>
                                            {cityName}
                                        </MenuItem>
                                    );
                                })
                            )}
                        </Select>
                    </FormControl>

                    {/* Date Picker Row */}
                    <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
                        <TextField
                            id="date-picker"
                            label="Select Date"
                            type="date"
                            value={dateInput}
                            onChange={(e) => setDateInput(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            disabled={!selectedCity}
                            sx={{ flexGrow: 1, minWidth: 200 }}
                        />
                        <Tooltip title="Add to list">
                            <span>
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddDate}
                                    disabled={!selectedCity || !dateInput}
                                    sx={{ height: 56 }}
                                >
                                    Add Date
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>

                    {/* Pending Dates Chips */}
                    {pendingDates.length > 0 && (
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Dates to be saved ({pendingDates.length}):
                            </Typography>
                            <Stack direction="row" flexWrap="wrap" gap={1}>
                                {pendingDates.map((date) => (
                                    <Chip
                                        key={date}
                                        label={formatDate(date)}
                                        onDelete={() => handleRemovePendingDate(date)}
                                        color="primary"
                                        variant="outlined"
                                    />
                                ))}
                            </Stack>
                        </Box>
                    )}

                    {/* Save Button */}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                        disabled={!selectedCity || pendingDates.length === 0 || saving}
                        sx={{ alignSelf: "flex-start", minWidth: 160 }}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {saving ? "Saving…" : "Save Off Days"}
                    </Button>
                </Box>
            </Paper>

            {/* ── Off-Days Table ── */}
            <Paper sx={{ width: "100%", mb: 2 }}>
                <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 }, gap: 2, flexWrap: "wrap" }}>
                    <Typography variant="h6" component="div" sx={{ flex: "1 1 auto" }}>
                        {selectedCity
                            ? `Off-Days for ${selectedCity}`
                            : "Select a city above to view"}
                    </Typography>
                    <TextField
                        size="small"
                        placeholder="Search off-days…"
                        value={searchQuery}
                        onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ minWidth: 220 }}
                    />
                </Toolbar>

                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
                    <Tabs value={activeTab} onChange={handleTabChange} aria-label="offdays tabs">
                        <Tab label="Current" id="tab-current" aria-controls="tabpanel-current" />
                        <Tab label="Archive" id="tab-archive" aria-controls="tabpanel-archive" />
                    </Tabs>
                </Box>

                <TableContainer>
                    <Table sx={{ minWidth: 450 }} aria-label="off-days table" size="small">
                        <EnhancedTableHead
                            headCells={headCells}
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={(_, property) => handleRequestSort(property)}
                            showCheckbox={false}
                            rowCount={visibleOffDays.length}
                            numSelected={0}
                        />
                        <TableBody>
                            {offDaysLoading ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                        <CircularProgress size="1.5rem" />
                                    </TableCell>
                                </TableRow>
                            ) : !selectedCity ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                        <Alert severity="info" sx={{ justifyContent: "center" }}>
                                            Select a city to view its off-days.
                                        </Alert>
                                    </TableCell>
                                </TableRow>
                            ) : visibleOffDays.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                        <Alert severity="info" sx={{ justifyContent: "center" }}>
                                            {searchQuery
                                                ? "No off-days match your search."
                                                : activeTab === 0
                                                    ? `No current off-days configured for ${selectedCity}.`
                                                    : `No archived off-days found for ${selectedCity}.`}
                                        </Alert>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedOffDays.map((od) => (
                                    <TableRow
                                        key={`${od.city}-${od.date}`}
                                        hover
                                        sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                                    >
                                        <TableCell>{od.city}</TableCell>
                                        <TableCell>{formatDate(od.date)}</TableCell>
                                        <TableCell>
                                            {od.approvedBy ? (
                                                <Chip label="Approved" color="success" size="small" variant="outlined" />
                                            ) : (
                                                <Chip label="Pending" color="warning" size="small" variant="outlined" />
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {isAdmin && !od.approvedBy && (
                                                <Tooltip title="Approve off-day">
                                                    <IconButton
                                                        size="small"
                                                        color="success"
                                                        onClick={() => handleApproveClick(od)}
                                                    >
                                                        <CheckIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {isAdmin ? (
                                                <Tooltip title="Delete off-day">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeleteClick(od)}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title="You do not have permission to delete this off-day.">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        disabled
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePaginationControls
                    rowsPerPageOptions={[10, 25, 50]}
                    count={visibleOffDays.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                />
            </Paper>

            {/* ── Delete Confirmation Dialog ── */}
            <DeleteConfirmDialog
                open={deleteDialogOpen}
                selectedCount={deleteTarget ? 1 : 0}
                onCancel={() => setDeleteDialogOpen(false)}
                onConfirm={handleDeleteConfirm}
                itemLabel="off-day"
            />
        </Box>
    );
}